import { observer } from "mobx-react";
import { ArrowRight } from "lucide-react";
import type { Agv, Mission, MissionStatus } from "../types";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { store } from "../../store";

const statusConfig: Record<MissionStatus, { label: string; variant: BadgeProps["variant"] }> = {
    pending: { label: "Pending", variant: "secondary" },
    in_progress: { label: "In progress", variant: "lightBlue" },
    failed: { label: "Failed", variant: "lightRed" },
    completed: { label: "Completed", variant: "lightGreen" },
};

export type MissionCardProps = {
    mission: Mission;
    agvs: Agv[];
};

export const MissionCard = observer(({ mission, agvs }: MissionCardProps): JSX.Element => {
    const { selectedMissionId } = store.serviceAgv;
    const isSelected = selectedMissionId === mission.id;
    const status = statusConfig[mission.status];

    return (
        <button
            onClick={() => store.serviceAgv.setSelectedMissionId(mission.id)}
            className={cn(
                `border-border w-full cursor-pointer rounded-lg border p-3 text-left transition-all`,
                isSelected ? "bg-muted" : "bg-white hover:shadow-sm",
            )}
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div>
                        <div className="text-sm font-semibold">{mission.name}</div>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                            <span>{mission.from} </span>
                            <ArrowRight size={12} />
                            <span>{mission.to}</span>
                        </div>
                    </div>
                </div>
                <Badge variant={status.variant}>{status.label}</Badge>
            </div>
            <div className="mt-2 items-center justify-between gap-3">
                {agvs.map((agv) => (
                    <div key={agv.id}>
                        <div className="text-sm font-semibold">{agv.name}</div>
                        <div className="text-muted-foreground truncate text-xs">
                            {agv.manufacturer} {agv.model}
                        </div>
                        {agv.status === "moving" && (
                            <Progress value={agv.pathProgress * 100} max={100} className="mt-2" />
                        )}
                    </div>
                ))}
            </div>
        </button>
    );
});
